import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from '@reduxjs/toolkit';
import { RootState, ThunkApi } from 'state/store';
import { Status } from 'utils/customTypes';
import { SLICE_STATUS } from 'utils/constants';
import { RequestPermissions } from 'types/store/normalized';
import { updateRequest } from 'state/ActiveRequest/activeRequestSlice';

/* ============================== STATE SETUP =============================== */
interface RequestPermissionsState {
  status: Status;
  loadingRequestIds: string[];
  notFoundRequestIds: string[];
}

const initialState: RequestPermissionsState = {
  status: SLICE_STATUS.IDLE,
  loadingRequestIds: [],
  notFoundRequestIds: [],
};

const adapter = createEntityAdapter<RequestPermissions>();

const selectRequestPermissionsState = (state: RootState) =>
  state.normalized.requestPermissions;

/* ============================== HELPERS =================================== */
interface GetListParams {
  requestIds?: string[];
  pageSize?: number;
}

const NO_OP_RESPONSE: RequestPermissions[] = [];

function getNewRequestIdsToFetch(
  state: RootState,
  requestIds: string[]
): string[] {
  const { entities, notFoundRequestIds = [] } =
    selectRequestPermissionsState(state);

  const notFoundSet = new Set(notFoundRequestIds);

  return requestIds.filter((id) => {
    const isCached = Boolean(entities[id]);
    const isNotFound = notFoundSet.has(id);
    return !isCached && !isNotFound;
  });
}

/* ============================== REDUX THUNK =============================== */
function concurrencyCondition(): boolean {
  return true;
}

export const getRequestPermissions = createAsyncThunk<
  RequestPermissions[],
  GetListParams,
  ThunkApi
>(
  'normalized/requestPermissions/getRequestPermissions',
  async (
    { requestIds = [], pageSize = 100 },
    { getState, extra: { getRequestPermissionsApi } }
  ) => {
    const state = getState();

    const cleanedIds = requestIds.filter(
      (id): id is string => typeof id === 'string' && id.trim() !== ''
    );

    const idsToFetch = getNewRequestIdsToFetch(state, cleanedIds);
    if (idsToFetch.length === 0) {
      return NO_OP_RESPONSE;
    }

    const { data } = await getRequestPermissionsApi().getByIdsPost({
      requestIds: idsToFetch,
    });

    return data;
  },
  { condition: concurrencyCondition }
);

/* ================================= REDUCER ================================ */
const requestPermissionsSlice = createSlice({
  name: 'normalized/requestPermissions',
  initialState: adapter.getInitialState(initialState),
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getRequestPermissions.pending, (state, action) => {
        const { requestIds = [] } = action.meta.arg;
        state.status = SLICE_STATUS.LOADING;

        const current = new Set(state.loadingRequestIds);
        requestIds.forEach((id) => current.add(id));
        state.loadingRequestIds = Array.from(current);
      })
      .addCase(getRequestPermissions.rejected, (state, action) => {
        const { requestIds = [] } = action.meta.arg;

        state.status = SLICE_STATUS.FAILED;

        state.loadingRequestIds = state.loadingRequestIds.filter(
          (id) => !requestIds.includes(id)
        );
      })
      .addCase(getRequestPermissions.fulfilled, (state, action) => {
        const { requestIds = [] } = action.meta.arg;

        state.loadingRequestIds = state.loadingRequestIds.filter(
          (id) => !requestIds.includes(id)
        );

        adapter.upsertMany(state, action.payload);
        state.status = SLICE_STATUS.SUCCESS;

        if (requestIds.length > 0) {
          const returnedIds = action.payload.map((p) => p.id);
          const requestedNotFound = requestIds.filter(
            (id) => !returnedIds.includes(id)
          );

          if (requestedNotFound.length > 0) {
            const current = new Set(state.notFoundRequestIds);
            requestedNotFound.forEach((id) => current.add(id));
            state.notFoundRequestIds = Array.from(current);
          }
        }
      })
      .addCase(updateRequest.fulfilled, (state, action) => {
        const requestId = action.meta.arg.id;
        adapter.removeOne(state, requestId);
        state.notFoundRequestIds = state.notFoundRequestIds.filter(
          (id) => id !== requestId
        );
      });
  },
});

/* =============================== SELECTORS ================================ */
const defaultSelectors = adapter.getSelectors<RootState>(
  (state) => state.normalized.requestPermissions
);

const selectStatus = (state: RootState) =>
  state.normalized.requestPermissions.status;

export const requestPermissionsSelectors = {
  ...defaultSelectors,
  selectStatus,
};

export default requestPermissionsSlice.reducer;
